import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { AppCard, AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import _ from 'lodash';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import ResourceManagerSidebar from '../../../layout/screenSpecifics/Modules/ResourceManager/Sidebar/ResourceManagerSidebar';
import SettingsTabs from '../../../layout/screenSpecifics/Modules/ResourceManager/Settings/SettingsTabs';
import Translator from '../../../layout/components/helpers/Translator';
import EditorTranslatable from '../../../layout/components/form/Texts/EditorTranslatable';
import { reorder } from '../../../minions/forms';
import InputTranslatable from '@/layout/components/form/Inputs/InputTranslatable';
import * as moduleAction from '../../../store/actions/moduleAction';

export default function ModuleResourceManagerSettingsInternalObligationHomeScreen() {
  const { moduleBridgeHex, obligationHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData,
  );
  const [internalObligation, setInternalObligation] = useState();
  const [addEvidenceAdvancedChecklist, setAddEvidenceAdvancedChecklist] =
    useState({});
  const [validationSchema] = useState(() =>
    Yup.object().shape({
      obligation: Yup.object()
        .required(
          ReactDOMServer.renderToString(
            <Translator file="user" name="user.required" />,
          ),
        )
        .test(
          'required-lang',
          ReactDOMServer.renderToString(
            <Translator file="user" name="user.required" />,
          ),
          (obj) => {
            const lang = 'es';
            const v = (obj?.[lang] ?? '').toString().trim();
            return v.length > 0;
          },
        )
        .test(
          'min-2-lang',
          ReactDOMServer.renderToString(
            <Translator file="user" name="user.min.2.characters" />,
          ),
          (obj) => {
            const lang = 'es';
            const v = (obj?.[lang] ?? '').toString().trim();
            if (v === '<p><br></p>') return false;
            const plain = v.replace(/<[^>]*>/g, '').trim();
            return plain.length >= 2;
          },
        ),
    }),
  );

  // PARA CAMBIAR A FUTURO A COMPONENTE O SIMILAR
  if (!moduleBridgeHex || !moduleData) {
    <AppNoInfo />;
  }
  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath('api_r_user_module_resourceManager_getModuleData'),
        'moduleData',
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    !moduleData && loadModuleData();
  }, []);
  // > LOAD MODULE DATA //
  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_resourceManager_getPermissions'),
          'permissions',
          {
            moduleBridgeHex,
            subType: 'permissions',
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_resourceManager_getPermissions'),
          'userBridge',
          {
            moduleBridgeHex,
            subType: 'userBridge',
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);
  // < STORE CALLS

  // > LOAD INTERNAL OBLIGATION
  const loadInternalObligation = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_settings_internalObligation_get',
          ),
          {
            moduleBridgeHex,
            obligationHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) setInternalObligation(data);
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, obligationHex],
  );

  useEffect(() => {
    loadInternalObligation();
  }, []);
  // < LOAD INTERNAL OBLIGATION

  const handleSave = useCallback(
    (payload) =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_settings_internalObligation_edit',
          ),
          {
            moduleBridgeHex,
            payload,
            obligationHex,
          },
        )
        .then(() => {
          if (isMounted.current) {
            navigate(
              path({
                name: 'user_module_resourceManager_settings_internalObligations',
                params: { moduleBridgeHex },
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, obligationHex],
  );

  const handleResortAdvanceEvidences = useCallback(
    (evidenceAdvancedChecklist, result, sfv) => {
      if (!result?.destination) return;

      if (
        result.destination.droppableId === result.source.droppableId &&
        result.destination.index === result.source.index
      ) {
        return;
      }

      const newOrder = reorder(
        evidenceAdvancedChecklist,
        result.source.index,
        result.destination.index,
      );

      sfv(
        'evidenceAdvancedChecklist',
        newOrder.map((eac, i) => ({ ...eac, sort: i })),
      );
    },
    [],
  );

  {
    /* <AppNoInfo text="Cargando..." minHeight={250} /> */
  }

  return (
    <>
      <ResourceManagerSidebar activeRoute="user_module_resourceManager_settings_internalObligations" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData?.moduleName}`,
                  route: {
                    name: 'user_module_resourceManager_resource_home',
                    params: {
                      moduleBridgeHex,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-resourceManager"
                      name="resource.configuration"
                    />
                  ),
                  route: {
                    name: 'user_module_resourceManager_settings_internalObligations',
                    params: {
                      moduleBridgeHex,
                    },
                  },
                },
                {
                  name: `${(
                    <Translator
                      file="module-resourceManager"
                      name="resource.internal.obligation"
                    />
                  )} (${obligationHex})`,
                },
              ]
            : [
                {
                  name: '...',
                },
              ]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <SettingsTabs
            moduleBridgeHex={moduleBridgeHex}
            activeTab="internalObligations"
          />
          <AppCard>
            {internalObligation ? (
              <Formik
                initialValues={{
                  obligation: internalObligation?.obligation,
                  nextDate: internalObligation?.nextDate,
                  evidenceAdvancedChecklist:
                    internalObligation?.evidenceAdvancedChecklist,
                }}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={validationSchema}
                onSubmit={handleSave}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  errors,
                  isSubmitting,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div className="row mb-2">
                      <div className="col-lg-12">
                        <EditorTranslatable
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="obligation-text"
                            />,
                          )}
                          icon={<AppRemixIcon icon="file-text" />}
                          onChange={(opt) => setFieldValue('obligation', opt)}
                          value={values.obligation}
                          error={errors.obligation}
                          className=""
                          toolbar={{
                            toolbar: [
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ color: [] }, { background: [] }],
                              ['clean'],
                              [{ direction: 'rtl' }],
                            ],
                          }}
                          height={200}
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-lg-6">
                        <h5>
                          <Translator
                            file="module-resourceManager"
                            name="resource.checklist"
                          />
                        </h5>
                        <AdvancedEvidenceList>
                          <DragDropContext
                            onDragEnd={(result) =>
                              handleResortAdvanceEvidences(
                                values?.evidenceAdvancedChecklist,
                                result,
                                setFieldValue,
                              )
                            }
                          >
                            <Droppable droppableId="droppable">
                              {(providedDroppable) => (
                                <ul
                                  {...providedDroppable.droppableProps}
                                  ref={providedDroppable.innerRef}
                                  className="list-unstyled m-0 p-0"
                                >
                                  <FieldArray
                                    name="evidenceAdvancedChecklist"
                                    render={(evidenceAdvancedChecklist) => (
                                      <>
                                        {values?.evidenceAdvancedChecklist?.map(
                                          (eac, i) => (
                                            <Draggable
                                              key={
                                                eac.id ??
                                                eac.tempId ??
                                                `row-${i}`
                                              } // key estable ideal: eac.id o tempId
                                              draggableId={String(
                                                eac.id ??
                                                  eac.tempId ??
                                                  `row-${i}`,
                                              )} // id estable
                                              index={i}
                                            >
                                              {(providedDraggable) => (
                                                <li
                                                  ref={
                                                    providedDraggable.innerRef
                                                  }
                                                  {...providedDraggable.draggableProps}
                                                  className="mb-3"
                                                >
                                                  {/* Arrastre solo desde el icono */}
                                                  <span
                                                    {...providedDraggable.dragHandleProps}
                                                  >
                                                    <AppRemixIcon
                                                      icon="arrow-up-down"
                                                      className="mb-3"
                                                      title={ReactDOMServer.renderToString(
                                                        <Translator
                                                          file="module-resourceManager"
                                                          name="resource.reorder"
                                                        />,
                                                      )}
                                                    />
                                                  </span>

                                                  <InputTranslatable
                                                    className="name mb-3"
                                                    onChange={(v) =>
                                                      setFieldValue(
                                                        `evidenceAdvancedChecklist[${i}].name`,
                                                        v,
                                                      )
                                                    }
                                                    values={
                                                      values
                                                        ?.evidenceAdvancedChecklist?.[
                                                        i
                                                      ]?.name
                                                    }
                                                    error={
                                                      errors
                                                        ?.evidenceAdvancedChecklist?.[
                                                        i
                                                      ]?.name
                                                    }
                                                  />

                                                  <button
                                                    type="button"
                                                    title={ReactDOMServer.renderToString(
                                                      <Translator
                                                        file="module-resourceManager"
                                                        name="resource.delete"
                                                      />,
                                                    )}
                                                    className="btn btn-sm btn-danger mb-3"
                                                    onClick={() =>
                                                      evidenceAdvancedChecklist.remove(
                                                        i,
                                                      )
                                                    }
                                                  >
                                                    <AppRemixIcon icon="delete-bin" />
                                                  </button>
                                                </li>
                                              )}
                                            </Draggable>
                                          ),
                                        )}

                                        {/* CLAVE: placeholder */}
                                        {providedDroppable.placeholder}
                                      </>
                                    )}
                                  />
                                </ul>
                              )}
                            </Droppable>
                          </DragDropContext>
                          <li className="mt-2">
                            <InputTranslatable
                              onChange={(v) =>
                                setAddEvidenceAdvancedChecklist(v)
                              }
                              values={addEvidenceAdvancedChecklist}
                            />
                            <button
                              type="button"
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-resourceManager"
                                  name="resource.add"
                                />,
                              )}
                              className="btn btn-sm btn-success mb-3"
                              disabled={_.isEmpty(addEvidenceAdvancedChecklist)}
                              onClick={() => {
                                values?.evidenceAdvancedChecklist.push({
                                  name: addEvidenceAdvancedChecklist,
                                  sort:
                                    values?.evidenceAdvancedChecklist?.length ||
                                    0,
                                });
                                setAddEvidenceAdvancedChecklist({});
                              }}
                            >
                              <AppRemixIcon icon="add" />
                            </button>
                          </li>
                        </AdvancedEvidenceList>
                      </div>
                      <div className="col-lg-6">
                        <EditorTranslatable
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="periodicity-text"
                            />,
                          )}
                          icon={<AppRemixIcon icon="file-text" />}
                          onChange={(opt) => setFieldValue('nextDate', opt)}
                          value={values.nextDate}
                          error={errors.nextDate}
                          className=""
                          toolbar={{
                            toolbar: [
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ color: [] }, { background: [] }],
                              ['clean'],
                              [{ direction: 'rtl' }],
                            ],
                          }}
                          height={200}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="mt-3 float-right">
                          <button
                            type="submit"
                            className="btn btn-xs btn-success"
                            disabled={isSubmitting}
                          >
                            <Translator
                              file="module-resourceManager"
                              name="resource.edit"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                )}
              </Formik>
            ) : (
              <AppNoInfo
                text={
                  <Translator
                    file="module-resourceManager"
                    name="resource.loading"
                  />
                }
                minHeight={250}
              />
            )}
          </AppCard>
        </div>
      </main>
    </>
  );
}

const AdvancedEvidenceList = styled.ul`
  list-style: none;
  padding: 0;
  li {
    display: flex;
    align-items: center;
    gap: 5px;
    & > div {
      flex-grow: 1;
    }
  }
`;
